import {
  m,
  useScroll,
  useTransform,
  useMotionTemplate,
  useReducedMotion,
  easeInOut,
  type MotionValue,
} from "framer-motion";
import {
  createContext,
  useContext,
  useRef,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";

/**
 * Scroll-linked lighting for a section. As the section enters the viewport the
 * returned motion values brighten/bloom — driving a `<LightRays />` overlay and
 * a heading glow so the section appears to gradually "switch on" as it scrolls
 * in.
 *
 * The opacity/brightness ramps are scroll-driven for everyone, including
 * reduced-motion users — a light fade/brighten isn't the vestibular motion that
 * `prefers-reduced-motion` guards against. Only actual movement (the rays'
 * scale "bloom") is dropped under reduced motion by `<LightRays />`.
 */
export function useScrollLight(ref: RefObject<HTMLElement | null>) {
  const { scrollYProgress } = useScroll({
    target: ref,
    // 0 = section top entering at viewport bottom, 1 = section top at viewport
    // top. The long travel (≈ one screen of scrolling) lets the light ramp up
    // gradually, like a dimmer being turned on as you scroll through.
    offset: ["start end", "start start"],
  });

  // Smooth eased ramps so the beams + heading glow slowly "warm up" rather than
  // snapping on.
  const raysOpacity = useTransform(scrollYProgress, [0, 1], [0, 1], { ease: easeInOut });
  const raysScale = useTransform(scrollYProgress, [0, 1], [0.86, 1.05], { ease: easeInOut });

  const glow = useTransform(scrollYProgress, [0, 1], [0, 22], { ease: easeInOut });
  const brightness = useTransform(scrollYProgress, [0, 1], [0.82, 1.16], { ease: easeInOut });
  const headingFilter = useMotionTemplate`brightness(${brightness}) drop-shadow(0 0 ${glow}px rgba(184,133,62,0.55))`;

  return { raysOpacity, raysScale, headingFilter };
}

/**
 * Gold stage-light beams that bloom behind a section. Sits absolutely at the top
 * of a `relative overflow-hidden` section, beneath the content.
 *
 * Opacity is scroll-driven for everyone; the scale "bloom" is only applied when
 * reduced motion is off.
 */
export function LightRays({
  opacity,
  scale,
  reduce,
  className = "",
}: {
  opacity: MotionValue<number>;
  scale: MotionValue<number>;
  reduce: boolean | null;
  className?: string;
}) {
  return (
    <m.div
      aria-hidden="true"
      // Opacity is scroll-driven for everyone (a brightness fade isn't the kind
      // of vestibular motion reduced-motion guards against). Only the scale
      // "bloom" — actual movement — is dropped under reduced motion.
      style={reduce ? { opacity } : { opacity, scale }}
      className={`light-rays pointer-events-none absolute inset-x-0 top-0 h-[78%] origin-top ${className}`}
    />
  );
}

/** Carries a `<LitSection>`'s scroll-driven heading glow to its `<LitHeading>`s. */
const LitHeadingContext = createContext<MotionValue<string> | null>(null);

/**
 * A full-width section pre-wired with scroll-linked stage lighting: gold beams
 * (`<LightRays />`) bloom in as it scrolls into view, and any `<LitHeading>`
 * descendant brightens in sync. Mirrors the home page's lit sections so the
 * effect can be reused across pages with a single wrapper.
 */
export function LitSection({
  className = "",
  style,
  bright = false,
  children,
}: {
  className?: string;
  style?: CSSProperties;
  /** Render the beams at boosted intensity for hero/CTA/feature sections. */
  bright?: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  const reduce = useReducedMotion();
  const { raysOpacity, raysScale, headingFilter } = useScrollLight(ref);
  return (
    <section ref={ref} className={`relative overflow-hidden ${className}`} style={style}>
      <LightRays
        opacity={raysOpacity}
        scale={raysScale}
        reduce={reduce}
        className={bright ? "light-rays--bright" : ""}
      />
      <LitHeadingContext.Provider value={headingFilter}>
        {children}
      </LitHeadingContext.Provider>
    </section>
  );
}

/**
 * A heading that glows in sync with its enclosing `<LitSection>`'s scroll
 * lighting. Renders a plain heading (no glow) if used outside a `<LitSection>`.
 */
export function LitHeading({
  as = "h2",
  className = "",
  children,
}: {
  as?: "h1" | "h2" | "h3";
  className?: string;
  children: ReactNode;
}) {
  const headingFilter = useContext(LitHeadingContext);
  const style = headingFilter ? { filter: headingFilter } : undefined;
  const MotionTag = as === "h1" ? m.h1 : as === "h3" ? m.h3 : m.h2;
  return (
    <MotionTag style={style} className={className}>
      {children}
    </MotionTag>
  );
}
